import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";

/* ═════════════════════════════════════════════════════════════════════
   Public testimonial submission — customers use this to submit their
   own quote when an admin sends them a `?ref=<uuid>` link.

   Rules baked in:
     • Requires explicit publication consent + accuracy confirmation.
     • Records `method_collected="customer_form"`, `verified_customer=true`
       (admin still has final approval before it goes public).
     • Compensation and relationship disclosures are collected up-front.
   ═════════════════════════════════════════════════════════════════════ */

export default function TestimonialSubmit() {
    const [params] = useSearchParams();
    const ref = params.get("ref") || "";
    const [f, setF] = useState({
        customer_name: "", customer_email: "", role: "", company: "",
        country: "", customer_type: "Individual", quote: "",
        display_consent: false, accuracy_confirmed: false,
        compensation_disclosure: "", relationship_disclosure: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

    useEffect(() => {
        if (!ref) return;
        // Admin can drop a hint via ref (e.g. prefill customer_type per plan)
        // — safe defaults, no PII fetched here.
    }, [ref]);

    const submit = async (e) => {
        e.preventDefault();
        if (!f.display_consent || !f.accuracy_confirmed) {
            toast.error("Please confirm accuracy and grant publication consent."); return;
        }
        if (f.quote.trim().length < 20) {
            toast.error("Please write at least a sentence or two."); return;
        }
        setSubmitting(true);
        try {
            await api.post("/public/testimonials/submit", { ...f, ref_token: ref });
            setDone(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PublicShell>
            <style>{styles}</style>
            <div className="ts-container" data-testid="testimonial-submit-page">
                <div className="ts-header">
                    <div className="ts-eyebrow">TESTIMONIAL SUBMISSION</div>
                    <h1 className="ts-title">Thank you for sharing your VAULT story.</h1>
                    <p className="ts-sub">
                        Your quote will be reviewed by our team before it appears publicly.
                        You can withdraw consent anytime by emailing privacy@vaultbypd.com.
                    </p>
                </div>

                {done ? (
                    <div className="ts-thanks" data-testid="ts-thanks">
                        <CheckCircle2 size={40} className="ts-thanks-icon" />
                        <h2>Received — thank you.</h2>
                        <p>An admin will review your submission and get back to you before publishing.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="ts-form" data-testid="ts-form">
                        <div className="ts-row">
                            <label><span>Full name</span>
                                <input required value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="ts-name" />
                            </label>
                            <label><span>Email</span>
                                <input required type="email" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
                            </label>
                        </div>
                        <div className="ts-row">
                            <label><span>Role</span>
                                <input value={f.role} onChange={(e) => set("role", e.target.value)} placeholder="e.g. Business owner" />
                            </label>
                            <label><span>Company (optional)</span>
                                <input value={f.company} onChange={(e) => set("company", e.target.value)} />
                            </label>
                        </div>
                        <div className="ts-row">
                            <label><span>Country</span>
                                <input required value={f.country} onChange={(e) => set("country", e.target.value)} />
                            </label>
                            <label><span>Customer type</span>
                                <select value={f.customer_type} onChange={(e) => set("customer_type", e.target.value)}>
                                    {["Individual", "Family", "Business", "Advisor"].map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </label>
                        </div>
                        <label className="ts-full">
                            <span>Your quote</span>
                            <textarea rows={4} required value={f.quote} onChange={(e) => set("quote", e.target.value)}
                                      placeholder="Tell us what VAULT changed for you — in your own words." data-testid="ts-quote" />
                        </label>
                        <label className="ts-full">
                            <span>Any compensation or incentive received? (leave blank if none)</span>
                            <input value={f.compensation_disclosure} onChange={(e) => set("compensation_disclosure", e.target.value)}
                                   placeholder="e.g. free access for 3 months" />
                        </label>
                        <label className="ts-full">
                            <span>Relationship to VAULT (leave blank if just a customer)</span>
                            <input value={f.relationship_disclosure} onChange={(e) => set("relationship_disclosure", e.target.value)}
                                   placeholder="e.g. beta programme participant" />
                        </label>
                        <label className="ts-check">
                            <input type="checkbox" checked={f.accuracy_confirmed} onChange={(e) => set("accuracy_confirmed", e.target.checked)} data-testid="ts-accuracy" />
                            <span>I confirm this quote is accurate and my own words.</span>
                        </label>
                        <label className="ts-check">
                            <input type="checkbox" checked={f.display_consent} onChange={(e) => set("display_consent", e.target.checked)} data-testid="ts-consent" />
                            <span>I grant VAULT permission to publish this quote on vaultbypd.com. I understand I can withdraw consent anytime.</span>
                        </label>
                        <button className="ts-submit" type="submit" disabled={submitting} data-testid="ts-submit">
                            {submitting ? "Submitting…" : (<>Submit for review <ArrowRight size={14} /></>)}
                        </button>
                    </form>
                )}
            </div>
        </PublicShell>
    );
}

const styles = `
.ts-container { max-width: 720px; margin: 0 auto; padding: 80px 28px 120px; color: #F6F3EC; }
@media (min-width: 768px) { .ts-container { padding-top: 120px; } }
.ts-header { text-align: center; margin-bottom: 44px; }
.ts-eyebrow { font-size: 10.5px; font-weight: 700; letter-spacing: 0.24em; color: #C6A86B; text-transform: uppercase; }
.ts-title { margin-top: 14px; font-size: 38px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; }
@media (min-width: 768px) { .ts-title { font-size: 48px; } }
.ts-sub { margin-top: 18px; font-size: 15px; line-height: 1.6; color: rgba(246,243,236,0.72); max-width: 560px; margin-left: auto; margin-right: auto; }
.ts-form { display: flex; flex-direction: column; gap: 16px; }
.ts-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 640px) { .ts-row { grid-template-columns: 1fr; } }
.ts-full { grid-column: 1 / -1; }
.ts-form label { display: flex; flex-direction: column; gap: 6px; font-size: 12px; font-weight: 600; color: rgba(246,243,236,0.72); }
.ts-form input, .ts-form select, .ts-form textarea { background: rgba(246,243,236,0.05); border: 1px solid rgba(246,243,236,0.1); color: #F6F3EC; padding: 12px 14px; border-radius: 10px; font-size: 14px; outline: none; font-family: inherit; }
.ts-form input:focus, .ts-form select:focus, .ts-form textarea:focus { border-color: #C6A86B; }
.ts-check { display: flex; gap: 10px; font-size: 13px; color: rgba(246,243,236,0.85); font-weight: 400; align-items: flex-start; }
.ts-check input { accent-color: #C6A86B; margin-top: 4px; }
.ts-submit { align-self: flex-start; margin-top: 12px; display: inline-flex; align-items: center; gap: 8px; padding: 14px 26px; border-radius: 999px; background: #C6A86B; color: #0B1122; font-size: 14px; font-weight: 700; border: none; cursor: pointer; }
.ts-submit:hover:not(:disabled) { background: #d3b978; }
.ts-submit:disabled { opacity: 0.6; }
.ts-thanks { text-align: center; padding: 60px 20px; background: rgba(41,197,138,0.06); border: 1px solid rgba(41,197,138,0.2); border-radius: 24px; }
.ts-thanks-icon { color: #29C58A; margin: 0 auto; }
.ts-thanks h2 { margin-top: 14px; font-size: 26px; font-weight: 800; }
.ts-thanks p { margin-top: 10px; color: rgba(246,243,236,0.72); }
`;
